/**
 * FullscreenLayout - Main layout for RoofMapper
 * Based on Electric Calculator design system
 */

'use client';

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Map,
  Box,
  MapPin,
  Sun,
  Loader2,
  Plus,
  Minus,
  Target,
  Image,
  Mountain,
  Check,
  X,
  Ruler,
  Trash2,
  Square,
  Eye,
} from 'lucide-react';
import { useRoofMapperContext } from '../../contexts/RoofMapperContext';
import { useMap } from '../../../contexts/MapContext';
import { useSolar } from '../../../contexts/SolarContext';
import { useAuth } from '../../../contexts/AuthContext';
import Map2DView from '../../../components/map/Map2DView';
import Map3DViewFinal from '../../../components/map/Map3DViewFinal';
import Map3DWithStreetViewControl from '../../../components/map/Map3DWithStreetViewControl';
import StreetView from '../../../components/map/StreetView';
import BuildingViewer3D from '../../../components/map/BuildingViewer3D';
import GeoTIFFViewer from '../../../components/solar/GeoTIFFViewer';
import GeoTIFFCanvasRenderer, { getProgressIcon } from '../../../components/solar/GeoTIFFCanvasRenderer';
import PlacesSearch from '../../../components/map/PlacesSearch';
import RPUSearchBar from '../../../components/map/RPUSearchBar';
import CacheStatusBadge from '../../../components/map/CacheStatusBadge';

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

export default function FullscreenLayout() {
  const {
    location,
    setLocation,
    mapMode,
    toggleMapMode,
    solarData,
    isLoadingSolar,
    selectedSegment,
    setSelectedSegment,
    features,
    theme,
  } = useRoofMapperContext();

  const { pinPosition, setPinPosition, cameraState, setCameraState, mapMode: contextMapMode, setMapMode: contextSetMapMode } = useMap();
  const { buildingInsights, dataLayers, fetchSolarData, clearSolarData, cacheInfo, isLoading: solarLoading } = useSolar();
  const { user } = useAuth();

  const [resultsExpanded, setResultsExpanded] = useState(false);
  const [showSolarPanel, setShowSolarPanel] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [solarSectionExpanded, setSolarSectionExpanded] = useState(true);
  const [lastFetchedPosition, setLastFetchedPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedRPU, setSelectedRPU] = useState<{
    rpu: string;
    address: string;
    latitude: number;
    longitude: number;
    cfe_address?: string;
  } | null>(null);
  
  // Solar visualization controls
  const [selectedSolarLayer, setSelectedSolarLayer] = useState<'rgb' | 'dsm' | 'flux'>('rgb');
  const [showMask, setShowMask] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({
    rgb: 'pending' as 'pending' | 'downloading' | 'complete' | 'error',
    mask: 'pending' as 'pending' | 'downloading' | 'complete' | 'error',
    dsm: 'pending' as 'pending' | 'downloading' | 'complete' | 'error',
    flux: 'pending' as 'pending' | 'downloading' | 'complete' | 'error',
  });

  // Handle place selection from search
  const handlePlaceSelected = (place: { lat: number; lng: number; address: string }) => {
    
    // Update pin position
    setPinPosition({ lat: place.lat, lng: place.lng });
    
    // Jump camera to location with 500m altitude for neighborhood view
    setCameraState({
      center: {
        lat: place.lat,
        lng: place.lng,
        altitude: 500  // High altitude for overview
      },
      tilt: 0,  // Bird's eye view
      heading: 0,
      range: 2000,  // Match current user setting
    });
  };

  // Handle RPU selection from search
    const handleSelectRPU = (result: { rpu: string; address: string; latitude: number; longitude: number; cfe_address?: string }) => {
      setSelectedRPU(result);
    
    // Update pin position
    setPinPosition({ lat: result.latitude, lng: result.longitude });
    
    // Jump camera to RPU location
    setCameraState({
      center: {
        lat: result.latitude,
        lng: result.longitude,
        altitude: 500
      },
      tilt: 0,
      heading: 0,
      range: 2000,
    });
  };

  // Handle Solar API fetch - only fetch if coordinates changed
  const handleGetSolarData = async () => {
    if (!pinPosition) return;
    
    // Check if we've already fetched data for this exact position
    if (lastFetchedPosition && 
        lastFetchedPosition.lat === pinPosition.lat && 
        lastFetchedPosition.lng === pinPosition.lng) {
      setShowSolarPanel(!showSolarPanel); // Toggle instead of always showing
      return;
    }

    // New location - fetch data
    try {
      await fetchSolarData(pinPosition);
      setLastFetchedPosition({ lat: pinPosition.lat, lng: pinPosition.lng });
      setShowSolarPanel(true); // Show the solar panel when data is loaded
      setResultsExpanded(true);
    } catch (error) {
      console.error('Failed to fetch solar data:', error);
    }
  };

  // Toggle Solar View (don't close, just hide/show)
  const toggleSolarView = () => {
    setShowSolarPanel(!showSolarPanel);
  };

  return (
    <div className="flex flex-col h-screen w-screen bg-gray-50 overflow-hidden">
      {/* Fixed Navbar */}
      <nav className="fixed top-0 left-0 right-0 h-20 bg-white/80 backdrop-blur border-b border-gray-200 z-50">
        <div className="h-full px-6 flex items-center justify-between">
          {/* Left: Logo + Title */}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-[#EBFD03] rounded-lg flex items-center justify-center">
              <span className="text-2xl font-bold text-black">N</span>
            </div>
            <h1 className="text-2xl font-bold text-black">RoofMapper</h1>
          </div>

          {/* Right: User Info */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Welcome, <span className="font-semibold">{user?.username || 'User'}</span>
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="flex flex-1 pt-20">
        {/* Fixed Sidebar */}
        <aside className="fixed top-20 left-0 bottom-0 w-[285px] bg-white border-r border-gray-200 overflow-y-auto z-40">
          <div className="p-6 space-y-6">
            {/* Section: Location Search */}
            {features.search && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-gray-600" />
                  Search Location
                </h3>

                {/* RPU Search Bar - NEW! */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    🔍 Search by RPU or Address
                  </label>
                  <RPUSearchBar 
                    onSelectRPU={handleSelectRPU}
                    placeholder="Search RPU number or address..."
                  />
                  
                  {/* Show selected RPU */}
                  {selectedRPU && (
                    <div className="mt-3 p-3 bg-yellow-100 border-2 border-yellow-400 rounded-lg">
                      <div className="text-xs font-bold text-gray-700 mb-1">
                        📍 SELECTED RPU
                      </div>
                      <div className="font-bold text-gray-900 text-sm">
                        {selectedRPU.rpu}
                      </div>
                      {selectedRPU.cfe_address && (
                        <div className="mt-2">
                          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Dirección CFE</div>
                          <div className="text-xs text-gray-800 font-medium">{selectedRPU.cfe_address}</div>
                        </div>
                      )}
                      {selectedRPU.address && (
                        <div className="mt-2">
                          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Dirección Geo</div>
                          <div className="text-xs text-gray-500">{selectedRPU.address}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-xs text-gray-500 font-medium">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Google Places Autocomplete */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    🌍 Search by Coordinates or Place
                  </label>
                <PlacesSearch 
                  onPlaceSelected={handlePlaceSelected}
                  disabled={isLoadingSolar}
                />
                </div>

                {/* Current Position Card */}
                {pinPosition && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-xs font-semibold text-blue-700 mb-2">
                      📍 Current Position
                    </p>
                    <p className="text-xs font-mono text-blue-700">
                      Lat: {pinPosition.lat.toFixed(6)}
                    </p>
                    <p className="text-xs font-mono text-blue-700">
                      Lng: {pinPosition.lng.toFixed(6)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Section: Solar Analysis - Collapsible */}
            {features.solarAnalysis && (
              <div className="space-y-3 pt-6 border-t border-gray-200">
                {/* Header with collapse toggle */}
                <button
                  onClick={() => setSolarSectionExpanded(!solarSectionExpanded)}
                  className="w-full flex items-center justify-between hover:bg-gray-50 p-2 rounded-lg transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Sun className="h-5 w-5 mr-2 text-gray-600" />
                    Solar Analysis
                  </h3>
                  {solarSectionExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-600" />
                  )}
                </button>

                {/* Toggle Buttons - Always Visible */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowMap(!showMap)}
                    disabled={!buildingInsights}
                    className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      showMap
                        ? 'bg-[#EBFD03] text-black'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-200`}
                  >
                    <Map className="h-4 w-4 mr-2" />
                    {showMap ? 'Hide' : 'Show'} Map
                  </button>
                  
                  {buildingInsights && (
                    <button
                      onClick={toggleSolarView}
                      className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        showSolarPanel
                          ? 'bg-[#EBFD03] text-black'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Sun className="h-4 w-4 mr-2" />
                      {showSolarPanel ? 'Hide' : 'Show'} Solar
                    </button>
                  )}
                </div>

                {/* Collapsible Content */}
                {solarSectionExpanded && (
                  <>
                    {/* Primary Action Button */}
                    <button
                      onClick={handleGetSolarData}
                      disabled={!pinPosition || isLoadingSolar}
                      className="w-full inline-flex items-center justify-center px-4 py-3
                               bg-[#EBFD03] text-black rounded-lg hover:bg-yellow-400
                               transition-colors font-semibold disabled:opacity-50
                               disabled:cursor-not-allowed shadow-sm"
                    >
                      {isLoadingSolar ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : buildingInsights ? (
                        <>
                          <Sun className="h-5 w-5 mr-2" />
                          Re-fetch Solar Data
                        </>
                      ) : (
                        <>
                          <Sun className="h-5 w-5 mr-2" />
                          Get Solar Data
                        </>
                      )}
                    </button>
                    
                    {/* Solar Layers with Inline Progress */}
                    {buildingInsights && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-gray-700">Solar Layers:</p>
                        
                        {[
                          { id: 'rgb', label: 'RGB Imagery', icon: <Image className="h-4 w-4" /> },
                          { id: 'dsm', label: 'DSM (Height)', icon: <Mountain className="h-4 w-4" /> },
                          { id: 'flux', label: 'Solar Flux', icon: <Sun className="h-4 w-4" /> },
                        ].map((layer) => {
                          const progress = downloadProgress[layer.id as keyof typeof downloadProgress];
                          const isSelected = selectedSolarLayer === layer.id;
                          const isComplete = progress === 'complete';
                          
                          return (
                            <button
                              key={layer.id}
                              onClick={() => setSelectedSolarLayer(layer.id as any)}
                              disabled={!isComplete}
                              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                                isSelected
                                  ? 'bg-[#EBFD03] text-black'
                                  : 'bg-white text-gray-700 hover:bg-gray-100'
                              } disabled:opacity-50 disabled:cursor-not-allowed border-2 ${
                                isSelected ? 'border-black' : 'border-gray-200'
                              }`}
                            >
                              <div className="flex items-center">
                                {layer.icon}
                                <span className="ml-2">{layer.label}</span>
                              </div>
                              <div className="ml-auto pl-2">
                                {getProgressIcon(progress)}
                              </div>
                            </button>
                          );
                        })}

                        {/* Mask Checkbox with Progress */}
                        <label className="flex items-center justify-between gap-2 cursor-pointer px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={showMask}
                              onChange={(e) => setShowMask(e.target.checked)}
                              disabled={downloadProgress.mask !== 'complete'}
                              className="w-4 h-4 rounded border-gray-300 text-[#EBFD03] focus:ring-[#EBFD03]"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              Show Roof Mask
                            </span>
                          </div>
                          <div>
                            {getProgressIcon(downloadProgress.mask)}
                          </div>
                        </label>
                      </div>
                    )}

                {/* Solar Results */}
                <div className="space-y-3 mt-4">
                  {/* Cache Status Badge */}
                  <CacheStatusBadge 
                    cacheInfo={cacheInfo}
                    isLoading={solarLoading}
                    dataQuality={cacheInfo?.dataQuality}
                    solarAvailable={cacheInfo?.solarAvailable}
                  />

                  {/* Building Insights Found Indicator */}
                  {buildingInsights && (
                    <div className="flex items-center gap-3 px-3 py-3 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-lg">
                      <div className="flex-shrink-0">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-900">Building Insights Found</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {buildingInsights.solarPotential.roofSegmentStats.length} roof segments •{' '}
                          {buildingInsights.solarPotential.maxArrayPanelsCount} max panels
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            )}

            {/* Instructions */}
            <div className="pt-6 border-t border-gray-200">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-900 mb-2">💡 How to use</p>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li>1. Click on the map to place a pin</li>
                  <li>2. Click "Get Solar Data" to analyze</li>
                  <li>3. Toggle between 2D and 3D views</li>
                  <li>4. View results in the panel below</li>
                </ul>
              </div>
            </div>
          </div>
        </aside>

        {/* Split Screen: Map + Building Insights Visualization */}
        <div className="flex-1 ml-[285px] flex">
          {/* Left: 2D/3D/Street View Map with Smooth Transition */}
          {showMap && (
          <div className={`${showSolarPanel ? 'w-1/2' : 'flex-1'} relative bg-gray-200`}>
            {/* 2D Map - Only render when active */}
            {contextMapMode === '2D' && (
              <div className="absolute inset-0 z-10">
                <Map2DView drawingMode="none" />
              </div>
            )}

            {/* Street View - Only render when active */}
            {contextMapMode === 'STREET' && (
              <div className="absolute inset-0 z-10">
                <StreetView />
              </div>
            )}

            {/* 3D Map with Street View Pegman - Only render when active */}
            {contextMapMode === '3D' && (
              <div className="absolute inset-0 z-10">
                <Map3DWithStreetViewControl drawingMode="none" />
              </div>
            )}

            {/* 2D/Street View/3D Toggle Buttons - Single Column */}
            <div className="absolute top-16 right-4 z-[100]">
              <div className="bg-white border-2 border-black rounded-lg shadow-xl overflow-hidden" style={{ width: '48px' }}>
                {/* 2D Button */}
                <button
                  onClick={() => contextSetMapMode('2D')}
                  className={`w-full p-2 transition-colors border-b-2 border-black cursor-pointer ${
                    contextMapMode === '2D'
                      ? 'bg-[#EBFD03] text-black'
                      : 'bg-white hover:bg-gray-100 text-gray-900'
                  } flex items-center justify-center`}
                  title="2D Map"
                >
                  <MapPin className="h-5 w-5" />
                </button>

                {/* Street View Button */}
                <button
                  onClick={() => contextSetMapMode('STREET')}
                  className={`w-full p-2 transition-colors border-b-2 border-black cursor-pointer ${
                    contextMapMode === 'STREET'
                      ? 'bg-[#EBFD03] text-black'
                      : 'bg-white hover:bg-gray-100 text-gray-900'
                  } flex items-center justify-center`}
                  title="Street View"
                >
                  <Eye className="h-5 w-5" />
                </button>

                {/* 3D Button */}
                <button
                  onClick={() => contextSetMapMode('3D')}
                  className={`w-full p-2 transition-colors cursor-pointer ${
                    contextMapMode === '3D'
                      ? 'bg-[#EBFD03] text-black'
                      : 'bg-white hover:bg-gray-100 text-gray-900'
                  } flex items-center justify-center`}
                  title="3D Map"
                >
                  <Box className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          )}

          {/* Right: GeoTIFF Canvas Renderer */}
          {buildingInsights && showSolarPanel && (
            <div className={`${showMap ? 'w-1/2' : 'flex-1'} bg-gray-900 border-l-4 border-[#EBFD03] relative`}>
              <GeoTIFFCanvasRenderer 
                key={`${buildingInsights.center.latitude}-${buildingInsights.center.longitude}`}
                buildingInsights={buildingInsights}
                dataLayers={dataLayers}
                selectedLayer={selectedSolarLayer}
                showMask={showMask}
                onProgressUpdate={(progress) => setDownloadProgress(progress)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Layer Toggle Button Component
interface LayerToggleButtonProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onToggle: () => void;
}

function LayerToggleButton({ label, icon, active, onToggle }: LayerToggleButtonProps) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors',
        active
          ? 'bg-[#EBFD03] text-black border-black'
          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
      )}
    >
      <div className={cn('h-4 w-4', active ? 'text-black' : 'text-gray-600')}>{icon}</div>
      <span className="flex-1 text-left">{label}</span>
      {active && <Check className="h-4 w-4" />}
    </button>
  );
}
